import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useMusicPlayer } from "@/context/MusicPlayerContext";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18n";
import TrackRow from "@/components/music/TrackRow";
import { toast } from "sonner";
import { Loader2, Play, User, UserCheck, UserPlus, DownloadCloud } from "lucide-react";

export default function ArtistDetail() {
  const { name } = useParams();
  const decoded = decodeURIComponent(name);
  const [data, setData] = useState(null);
  const [following, setFollowing] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [info, setInfo] = useState(null);
  const [bioOpen, setBioOpen] = useState(false);
  const { playQueue, setRadio } = useMusicPlayer();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const isAdmin = user?.role === "admin";

  const load = () => api.get(`/music/artists/${encodeURIComponent(decoded)}/tracks`).then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => {
    load();
    api.get("/music/follows").then((r) => setFollowing((r.data.names || []).includes(decoded))).catch(() => {});
    // The biography comes from Wikipedia and is cached server side, so this is
    // a cheap call after the first visit.
    api.get(`/music/artists/${encodeURIComponent(decoded)}`).then((r) => setInfo(r.data)).catch(() => setInfo(false));
    // `load` is rebuilt every render; the artist is refetched when the name
    // in the URL changes, not on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decoded]);

  const getAllSongs = async () => {
    setFetching(true);
    try {
      const { data: res } = await api.post("/music/artist-fetch", { artist: decoded });
      toast.success(`${res.added} ${t("added_n")}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setFetching(false); }
  };

  const toggleFollow = async () => {
    try {
      if (following) { await api.delete(`/music/follows/${encodeURIComponent(decoded)}`); setFollowing(false); toast.success(t("unfollowed")); }
      else { await api.post("/music/follows", { artist: decoded }); setFollowing(true); toast.success(t("following")); }
      window.dispatchEvent(new Event("follows-changed"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const startRadio = () => { if (data && data.tracks.length) { setRadio && setRadio(true); playQueue(data.tracks, 0); toast.success(t("radio_on")); } };

  if (data === null) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  if (data === false) return <div className="p-10 text-zinc-400">{t("artist_not_found")}</div>;

  // The Deezer photo beats a track thumbnail when we have one.
  const photo = (info && info.thumbnail) || data.tracks[0]?.thumbnail;
  // Show the biography in the reader's language, falling back to the other.
  const bio = info ? (lang === "es" ? info.bio_es || info.bio_en : info.bio_en || info.bio_es) : null;

  return (
    <div>
      <div className="p-4 md:p-8 pb-6 bg-gradient-to-b from-fuchsia-700/30 to-transparent flex items-end gap-6">
        {photo ? <img src={photo} alt="" className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover shadow-2xl shrink-0" /> : <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-white/10 flex items-center justify-center shrink-0"><User className="w-16 h-16 text-zinc-500" /></div>}
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider font-bold">{t("artist")}</p>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2">{decoded}</h1>
          <p className="text-zinc-300 mt-3 text-sm">{data.tracks.length} {t("tracks")}</p>
          {bio && (
            <div className="mt-3 max-w-3xl">
              <p className={`text-sm text-zinc-300 leading-relaxed ${bioOpen ? "" : "line-clamp-3"}`}>
                {bio}
              </p>
              <div className="flex items-center gap-3 mt-1.5">
                <button
                  onClick={() => setBioOpen((v) => !v)}
                  data-testid="artist-bio-toggle"
                  className="text-xs font-semibold text-[#FF2A4D] hover:underline"
                >
                  {bioOpen ? t("read_less") : t("read_more")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="px-8 py-4 flex items-center gap-3 flex-wrap">
        <button data-testid="artist-play-all" onClick={() => data.tracks.length && playQueue(data.tracks, 0)} className="w-14 h-14 rounded-full bg-[#FF2A4D] hover:bg-[#E61C3D] hover:scale-105 flex items-center justify-center transition-all">
          <Play className="w-6 h-6 text-white ml-1" fill="white" />
        </button>
        <button data-testid="follow-btn" onClick={toggleFollow} className={`rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 border transition-colors ${following ? "border-[#FF2A4D] text-[#FF2A4D]" : "border-white/20 text-white hover:border-white/50"}`}>
          {following ? <><UserCheck className="w-4 h-4" /> {t("following")}</> : <><UserPlus className="w-4 h-4" /> {t("follow")}</>}
        </button>
        <button data-testid="artist-radio-btn" onClick={startRadio} className="rounded-full px-5 py-2.5 text-sm font-semibold border border-white/20 text-white hover:border-white/50 transition-colors">{t("radio")}</button>
        {isAdmin && (
          <button data-testid="get-all-songs" onClick={getAllSongs} disabled={fetching} className="rounded-full px-5 py-2.5 text-sm font-semibold bg-white/10 hover:bg-white/20 flex items-center gap-2 transition-colors disabled:opacity-60">
            {fetching ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("getting_songs")}</> : <><DownloadCloud className="w-4 h-4" /> {t("get_all_songs")}</>}
          </button>
        )}
      </div>
      <div className="px-5 pb-6">
        {data.tracks.map((tr, i) => (
          <TrackRow key={tr.id} track={tr} index={i} tracks={data.tracks} />
        ))}
      </div>
    </div>
  );
}
